import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/components/theme-provider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faHouse,
  faFileLines,
  faCalendar,
  faChartBar,
  faGear,
  faMagnifyingGlass,
  faUsers,
  faChevronRight,
  faRocket,
  faBug,
  faCrown,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import { PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/auth-context";
import { useSubscription } from "@/contexts/subscription-context";
import { UpgradeDialog } from "@/components/common/UpgradeDialog";
import { BrandSwitcher } from "@/components/dashboard/BrandSwitcher";
import { TalkToUs } from "@/components/dashboard/TalkToUs";
import { TrialStatusMessage } from "@/components/billing/TrialStatusMessage";
import { useStrategySessionModal } from "@/hooks/useStrategySessionModal";
import posthog from "posthog-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Types
type NavItem = {
  title: string;
  href: string;
  icon: IconDefinition;
  subItems?: NavItem[];
};

// Data
const navItems: NavItem[] = [
  {
    title: "Dashboard",
    href: "/",
    icon: faHouse,
  },
  {
    title: "Articles",
    href: "/articles",
    icon: faFileLines,
  },
  {
    title: "Content Calendar",
    href: "/content/calendar",
    icon: faCalendar,
  },
  {
    title: "SEO",
    href: "/seo",
    icon: faMagnifyingGlass,
    subItems: [
      {
        title: "Overview",
        href: "/seo/overview",
        icon: faChartBar,
      },
      {
        title: "PageSpeed",
        href: "/seo/pagespeed",
        icon: faRocket,
      },
      {
        title: "Enhancements",
        href: "/seo/enhancements",
        icon: faBug,
      },
    ],
  },
  {
    title: "Analytics",
    href: "/analytics",
    icon: faChartBar,
  },
];

const moreItems: NavItem[] = [
  {
    title: "Integrations",
    href: "/integrations",
    icon: faGear,
  },
  {
    title: "Brand",
    href: "/brand",
    icon: faUsers,
  },
  {
    title: "Free Strategy Session",
    href: "#",
    icon: faCalendarCheck,
  },
  {
    title: "Settings",
    href: "/settings",
    icon: faGear,
  },
];

// Article Quota Display Component
function ArticleQuotaDisplay() {
  const { subscription } = useSubscription();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  if (!subscription || subscription.status !== "active") return null;

  const { articlesQuota } = subscription;
  const percentage = articlesQuota.isLimited
    ? Math.min(
        100,
        Math.round((articlesQuota.used / articlesQuota.limit) * 100),
      )
    : 0;
  const isHighUsage = percentage >= 75;

  // Determine color based on usage
  const getProgressColor = () => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-amber-500";
    return "bg-green-500";
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">Monthly Article Quota</span>
        <span
          className={cn(
            "font-medium",
            percentage >= 90
              ? "text-red-500"
              : percentage >= 75
                ? "text-amber-500"
                : "text-green-500",
          )}
        >
          {articlesQuota.used} /{" "}
          {articlesQuota.isLimited ? articlesQuota.limit : "∞"}
        </span>
      </div>

      {articlesQuota.isLimited && (
        <>
          <Progress
            value={percentage}
            className="h-2"
            indicatorClassName={getProgressColor()}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">
              {articlesQuota.remaining === 0
                ? "You've used all your articles."
                : `${articlesQuota.remaining} articles remaining.`}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-6 px-2 text-xs gap-1",
                isHighUsage
                  ? "text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                  : "",
              )}
              onClick={() => setUpgradeDialogOpen(true)}
            >
              <FontAwesomeIcon icon={faCrown} className="h-3 w-3" />
              <span>Upgrade</span>
            </Button>
          </div>
        </>
      )}

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
      />
    </div>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const { user, logout } = useAuth() || {
    user: { name: "User", email: "user@example.com" },
    logout: () => {},
  };
  const navigate = useNavigate();
  const { isWithinPromoPeriod, hasBookedSession } = useStrategySessionModal();

  // Check if we're on a route that should show minimal sidebar
  const isMinimalSidebarRoute = [
    "/onboarding",
    "/start-trial",
    "/plans",
  ].includes(location.pathname);

  // Check if we're on any SEO page
  const isOnSeoPage = location.pathname.startsWith("/seo");

  const logoutHandler = () => {
    logout();
    navigate("/signin");
  };

  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <aside
      className={cn(
        "flex flex-col h-full backdrop-blur-sm transition-all duration-300",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-4">
        {!collapsed && (
          <div className="flex items-center gap-2 animate-fade-in">
            <img
              src={theme === "dark" ? "/logo-white.png" : "/logo.png"}
              alt="Creator Logo"
              className="h-8 w-auto"
            />
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover-lift"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-5 w-5 text-muted-foreground" />
          ) : (
            <PanelRightOpen className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>
      </div>

      {!collapsed && !isMinimalSidebarRoute && (
        <div className="mb-2 mt-2 px-4 animate-fade-in">
          <BrandSwitcher />
        </div>
      )}

      {/* Trial Status Message */}
      {!collapsed && !isMinimalSidebarRoute && (
        <div className="px-4 pb-2 animate-fade-in">
          <TrialStatusMessage />
        </div>
      )}

      {/* Article Quota Display */}
      {!collapsed && !isMinimalSidebarRoute && (
        <div className="px-4 py-2 animate-fade-in">
          <ArticleQuotaDisplay />
        </div>
      )}

      {/* Main navigation */}
      <div className="flex-1 overflow-y-auto">
        {!isMinimalSidebarRoute && (
          <div className="px-3 py-2">
            {!collapsed && (
              <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                Platform
              </p>
            )}
            <nav className="space-y-1 py-2">
              {navItems.map((item, index) => (
                <div key={item.href}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-all duration-200 hover-lift",
                      location.pathname === item.href ||
                        (item.title === "SEO" && isOnSeoPage)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-stone-50/80 hover:text-accent-foreground",
                      collapsed && "px-[10px]",
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      className={cn("w-5 h-5", !collapsed && "mr-2")}
                    />
                    {!collapsed && <span>{item.title}</span>}
                    {!collapsed && item.subItems && (
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className={cn(
                          "ml-auto w-3 h-3",
                          item.title === "SEO" &&
                            isOnSeoPage &&
                            "transform rotate-90 transition-transform",
                        )}
                      />
                    )}
                  </Link>

                  {/* Sub-items */}
                  {!collapsed &&
                    item.subItems &&
                    item.title === "SEO" &&
                    isOnSeoPage && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            className={cn(
                              "flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-all duration-200 hover-lift",
                              location.pathname === subItem.href
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-stone-50/80 hover:text-accent-foreground",
                            )}
                          >
                            <FontAwesomeIcon
                              icon={subItem.icon}
                              className="w-4 h-4 mr-2"
                            />
                            <span>{subItem.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </nav>
          </div>
        )}

        {/* Settings and other items */}
        {!isMinimalSidebarRoute && (
          <div className="px-3 py-2">
            {!collapsed && (
              <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                More
              </p>
            )}
            <nav className="space-y-1 py-2">
              {moreItems.map((item, index) => {
                if (item.title === "Free Strategy Session") {
                  // Only show if within promo period and hasn't booked yet
                  if (!isWithinPromoPeriod || hasBookedSession) return null;

                  return (
                    <button
                      key={item.href}
                      onClick={() => {
                        posthog.capture("strategy_session_booked", {
                          source: "sidebar",
                        });
                        window.open(
                          "https://cal.com/aicreator/30min",
                          "_blank",
                        );
                        // Mark as booked when clicked from sidebar
                        localStorage.setItem(
                          "hasBookedStrategySession",
                          "true",
                        );
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-all duration-200 hover-lift",
                        "text-muted-foreground hover:bg-stone-50/80 hover:text-accent-foreground",
                        collapsed && "px-[10px]",
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={cn("w-5 h-5", !collapsed && "mr-2")}
                      />
                      {!collapsed && <span>{item.title}</span>}
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-all duration-200 hover-lift",
                      location.pathname === item.href
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-stone-50/80 hover:text-accent-foreground",
                      collapsed && "px-[10px]",
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      className={cn("w-5 h-5", !collapsed && "mr-2")}
                    />
                    {!collapsed && <span>{item.title}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Talk to Us section */}
      {!collapsed && (
        <div className="px-3 pb-2">
          <TalkToUs />
        </div>
      )}

      {/* User profile section */}
      <div className="mt-auto border-t p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              className={cn(
                "flex items-center gap-3 rounded-lg px-2 py-2 cursor-pointer hover:bg-stone-50/80",
                collapsed ? "justify-center" : "justify-between",
              )}
            >
              <div className="flex items-center gap-2">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="text-white avatar-gradient">
                    {user?.name ? getInitials(user.name) : "U"}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {user?.name || "User"}
                    </span>
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                      {user?.email || "user@example.com"}
                    </span>
                  </div>
                )}
              </div>
              {!collapsed && (
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
                </Button>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="animate-fade-in w-56">
            <div className="flex items-center gap-2 p-2 border-b">
              <div className="flex-shrink-0">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="text-white avatar-gradient">
                    {user?.name ? getInitials(user.name) : "U"}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {user?.name || "User"}
                </span>
                <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                  {user?.email || "user@example.com"}
                </span>
              </div>
            </div>
            {/* <div className="p-2">
              <Link
                to="/upgrade"
                className="flex items-center gap-2 w-full rounded-md p-2 text-sm hover:bg-stone-50"
              >
                <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                <span>Upgrade to Pro</span>
              </Link>
            </div> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Link to="/settings" className="flex items-center gap-2 w-full">
                Account
              </Link>
            </DropdownMenuItem>
            {/* <DropdownMenuItem>
              <Link to="/billing" className="flex items-center gap-2 w-full">
                Billing
              </Link>
            </DropdownMenuItem> */}
            {/* <DropdownMenuItem>
              <Link
                to="/notifications"
                className="flex items-center gap-2 w-full"
              >
                Notifications
              </Link>
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logoutHandler}
              className="cursor-pointer"
            >
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
